// PreviewBridge: lets the admin's Website › Design workspace show an
// unpublished draft in the real public site (DESIGN §7.12).
//
// It is active only when ALL of these hold, so a fan can never see a draft:
//   - the page URL has ?__adm_preview=1,
//   - the page is framed (window.parent !== window),
//   - the parent is the same origin (the admin lives on the league's own host).
// Messages are accepted only from window.parent at this origin.
//
// Protocol (postMessage, same origin):
//   preview → admin  { type: 'adm-preview:ready' }
//   admin → preview  { type: 'adm-preview:draft', draft: PreviewDraft }
// The draft carries the *effective* values (published + draft) for what the
// tenant payload drives: the website template and its options, theme, site
// sections, copy, section titles, branding, logo and favicon. The template
// host reads `site_template` from the tenant this returns, so a drafted
// template swaps in the frame without a reload (ARCHITECTURE §10.2). The homepage layout is applied by answering the
// page's own GET /public/homepage with the draft's sections (the admin
// reloads the frame when the layout changes). Page-view tracking is dropped
// in preview so drafts never count as traffic.

import { useEffect, useMemo, useState } from 'react'
import type { TenantInfo } from './LeagueContext'
import type { ThemeColors } from '../services/api/types'
import { applyFaviconToDOM, applyThemeToDOM } from '../utils/theme'
import { setPreferStaffCredential } from '../services/publicAccess'

export interface PreviewLayoutItem {
  id: number
  section_type: string
  display_order: number
  mobile_display_order: number
  column_position: 'left' | 'right' | 'full'
  is_active: boolean
}

export interface PreviewDraft {
  /** The drafted website template, options filled in ('auto' stays 'auto': the host resolves it per sport). */
  site_template?: { id: string; options: Record<string, string | boolean> }
  theme?: ThemeColors
  site_sections?: Record<string, boolean>
  /** Website › Design › Registration (league-wide). */
  registration_site?: Record<string, boolean>
  copy?: Partial<TenantInfo['copy']>
  section_overrides?: TenantInfo['section_overrides']
  branding?: Partial<TenantInfo['branding']>
  logo_url?: string | null
  favicon_url?: string | null
  layout?: PreviewLayoutItem[]
}

export const PREVIEW_PARAM = '__adm_preview'

function detect(): boolean {
  try {
    if (typeof window === 'undefined' || window.parent === window) return false
    if (new URLSearchParams(window.location.search).get(PREVIEW_PARAM) !== '1') return false
    // Throws (cross-origin) unless the parent is this origin.
    return window.parent.location.origin === window.location.origin
  } catch {
    return false
  }
}

const ACTIVE = detect()
// An admin previewing a members-only league is admitted as staff (T7,
// registration step 33): the public client sends the staff token first in
// this frame, even if the browser also holds a member token whose account is
// not registered (services/publicAccess.ts).
if (ACTIVE) setPreferStaffCredential(true)
let current: PreviewDraft | null = null
let resolveFirst: (() => void) | null = null
const firstDraft: Promise<void> = new Promise((r) => {
  resolveFirst = r
  if (!ACTIVE) r()
  else setTimeout(r, 1500) // never hold the page if the admin doesn't answer
})
const listeners = new Set<(d: PreviewDraft) => void>()

if (ACTIVE) {
  window.addEventListener('message', (e: MessageEvent) => {
    if (e.source !== window.parent || e.origin !== window.location.origin) return
    const msg = e.data as { type?: string; draft?: PreviewDraft }
    if (msg?.type !== 'adm-preview:draft' || !msg.draft || typeof msg.draft !== 'object') return
    current = msg.draft
    resolveFirst?.()
    listeners.forEach((l) => l(msg.draft!))
  })
  // Answer the homepage with the draft layout; drop page-view tracking.
  const realFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    if (/\/public\/(track|page-views?)\b/.test(url)) return new Response(null, { status: 204 })
    if (/\/public\/homepage(\?|$)/.test(url)) {
      await firstDraft
      const res = await realFetch(input, init)
      if (!res.ok || !current?.layout) return res
      const body = (await res.json()) as Record<string, unknown>
      return new Response(JSON.stringify({ ...body, layout: current.layout.filter((s) => s.is_active) }), { status: res.status, headers: { 'content-type': 'application/json' } })
    }
    return realFetch(input, init)
  }
  window.parent.postMessage({ type: 'adm-preview:ready' }, window.location.origin)
}

function validSiteTemplate(st: PreviewDraft['site_template']): st is NonNullable<PreviewDraft['site_template']> {
  return !!st && typeof st.id === 'string' && !!st.options && typeof st.options === 'object' && !Array.isArray(st.options)
}

/** True inside the admin's Design preview frame. */
export function useIsDesignPreview(): boolean {
  return ACTIVE
}

/**
 * The tenant the public site should render: the published one, or (inside
 * the admin's preview frame) the published one with the draft laid over it.
 */
export function usePreviewTenant(league: TenantInfo | null): TenantInfo | null {
  const [draft, setDraft] = useState<PreviewDraft | null>(current)
  useEffect(() => {
    if (!ACTIVE) return
    listeners.add(setDraft)
    // A draft that arrived between the first render and now went only to
    // `current` (no listener yet), so the frame kept painting the published
    // theme after a reload (#610). Pick it up.
    if (current) setDraft(current)
    return () => void listeners.delete(setDraft)
  }, [])
  const merged = useMemo(() => {
    if (!ACTIVE || !draft || !league) return league
    return {
      ...league,
      ...(validSiteTemplate(draft.site_template) ? { site_template: { id: draft.site_template.id, options: { ...draft.site_template.options } } } : {}),
      theme: draft.theme ?? league.theme,
      site_sections: draft.site_sections ? { ...(league.site_sections ?? {}), ...draft.site_sections } : league.site_sections,
      registration_site: draft.registration_site ? { ...(league.registration_site ?? {}), ...draft.registration_site } : league.registration_site,
      copy: draft.copy ? { ...league.copy, ...draft.copy } : league.copy,
      section_overrides: draft.section_overrides ?? league.section_overrides,
      branding: draft.branding ? { ...league.branding, ...draft.branding } : league.branding,
      logo_url: draft.logo_url !== undefined ? draft.logo_url : league.logo_url,
      favicon_url: draft.favicon_url !== undefined ? draft.favicon_url : league.favicon_url,
    } as TenantInfo
  }, [league, draft])
  // Re-apply after every tenant refresh too (refreshTenant repaints the published theme).
  useEffect(() => {
    if (!ACTIVE || !merged) return
    if (merged.theme) applyThemeToDOM(merged.theme)
    if (merged.favicon_url) applyFaviconToDOM(merged.favicon_url)
  }, [merged])
  return merged
}
